/// <reference lib="dom" />
//
// Pipe calculator island (mobile-style port).
//
// Four sliders — temperature spread ΔT, average medium temperature, heat power,
// and run length — drive a live heat-gradient pipe (colour = temperature, width
// = length, glow = power). "See sizes" then matches the AustroPUR PEX tables and
// shows the suitable diameters with pressure drop + heat loss, plus Call / Mail
// to order. All client-side; tables + UI strings are embedded as JSON. See
// ../shared/pipeData.ts.

export {}; // module scope

type TempLoss = { insulation: string; dimension: string; uWert: number };
type PipeProduct = {
  variantId: number;
  sku: string; name: string; type: string; jacket: string; diameter: number;
  priceCents: number | null; currency: string; priceFallback: boolean;
  priceText: string; imageUrl: string | null; href: string;
};
type PipeConfigData = {
  dtColumns: number[];
  pipeColumns: number[];
  dtKw: number[][];
  kwPipe: (number | null)[][];
  tempLossSingle: TempLoss[];
  tempLossDuo: TempLoss[];
  products: PipeProduct[];
  contact: { phone: string; email: string };
  lang: string;
  strings: Record<string, string>;
};
type Pick = { diameter: number; dropPerM: number };

const DEFAULT_IMAGE =
  "https://www.ibeco.se/media/1s4osjtc/il_blue.jpg?rmode=pad&ranchor=center&width=600&height=600&quality=90&upscale=true&bgcolor=ffffff";

// Ground / ambient temperature assumed for the heat-loss estimate (the mobile
// flow drops the explicit input).
const GROUND_TEMP = 10;

const g = {
  data: document.getElementById("pc-data"),
  dt: document.getElementById("pc-dt") as HTMLInputElement | null,
  dtValue: document.getElementById("pc-dt-value"),
  avg: document.getElementById("pc-avg") as HTMLInputElement | null,
  avgValue: document.getElementById("pc-avg-value"),
  power: document.getElementById("pc-power") as HTMLInputElement | null,
  powerValue: document.getElementById("pc-power-value"),
  len: document.getElementById("pc-len") as HTMLInputElement | null,
  lenValue: document.getElementById("pc-len-value"),
  pipe: document.getElementById("pc-pipe"),
  visualSummary: document.getElementById("pc-visual-summary"),
  visualTemps: document.getElementById("pc-visual-temps"),
  results: document.getElementById("pc-results"),
};

type Els = {
  data: HTMLElement;
  dt: HTMLInputElement; dtValue: HTMLElement;
  avg: HTMLInputElement; avgValue: HTMLElement;
  power: HTMLInputElement; powerValue: HTMLElement;
  len: HTMLInputElement; lenValue: HTMLElement;
  pipe: HTMLElement; visualSummary: HTMLElement; visualTemps: HTMLElement;
  results: HTMLElement;
};

if (
  g.data && g.dt && g.dtValue && g.avg && g.avgValue && g.power && g.powerValue &&
  g.len && g.lenValue && g.pipe && g.visualSummary && g.visualTemps && g.results
) {
  boot(g as Els);
}

function boot(g: Els) {
  const data = parseData(g.data);
  const s = data.strings;
  const locale = data.lang === "de" ? "de-DE" : "en-US";
  const nf0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const nf1 = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const tr = (key: string, vars?: Record<string, string | number>) =>
    (s[key] ?? "").replace(/\{(\w+)\}/g, (_, k) => (vars && k in vars ? String(vars[k]) : `{${k}}`));

  g.dt.addEventListener("input", onChange);
  g.avg.addEventListener("input", onChange);
  g.power.addEventListener("input", onChange);
  g.len.addEventListener("input", onChange);

  // Delegated add-to-cart for matched-product cards (length goods → unitAmount = metres).
  g.results.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest("[data-add-cart]") as HTMLButtonElement | null;
    if (!btn) return;
    e.preventDefault();
    const vid = Number(btn.getAttribute("data-vid"));
    const len = Number(btn.getAttribute("data-len"));
    if (!vid || !(len > 0) || btn.disabled) return;
    void addToCart(vid, len, btn);
  });

  onChange();

  async function addToCart(variantId: number, unitAmount: number, btn: HTMLButtonElement) {
    btn.disabled = true;
    try {
      const r = await fetch("/cart/add", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
        body: new URLSearchParams({ variantId: String(variantId), qty: "1", unitAmount: String(unitAmount) }),
      });
      if (r.ok) {
        bumpCount(1);
        btn.innerHTML = `<i class="fa-solid fa-check"></i> ${s.prod_added || "Added"}`;
      }
    } catch {
      /* ignore */
    } finally {
      setTimeout(() => {
        btn.disabled = false;
      }, 1500);
    }
  }

  function bumpCount(n: number) {
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      const c = parseInt(el.textContent || "0", 10) || 0;
      el.textContent = String(c + n);
      (el as HTMLElement).classList.remove("hidden");
    });
  }

  function readDt(): number {
    const i = Math.min(data.dtColumns.length - 1, Math.max(0, Math.round(Number(g.dt.value))));
    return data.dtColumns[i] ?? data.dtColumns[0] ?? 0;
  }

  function onChange() {
    draw();
    renderResults();
  }

  // ---- live heat-gradient pipe ----------------------------------------------

  function draw() {
    const dt = readDt();
    const avg = Number(g.avg.value);
    const power = Number(g.power.value);
    const len = Number(g.len.value);
    const hot = avg;
    const cold = avg - dt;

    g.dtValue.textContent = `${dt} K`;
    g.avgValue.textContent = `${avg} °C`;
    g.powerValue.textContent = `${power} kW`;
    g.lenValue.textContent = `${len} m`;

    const widthPct = 35 + (clamp01((len - 10) / (200 - 10)) * 65); // 35%–100%
    const powerT = clamp01((power - 5) / (250 - 5));
    const opacity = 0.6 + powerT * 0.4;
    const glow = 2 + powerT * 8;
    const hotC = tempColor(hot);
    const coldC = tempColor(cold);

    g.pipe.style.width = `${widthPct}%`;
    g.pipe.style.background = `linear-gradient(90deg, ${hotC}, ${coldC})`;
    g.pipe.style.opacity = String(opacity);
    g.pipe.style.boxShadow = `0 0 ${glow}px ${hotC}`;

    g.visualSummary.textContent = tr("visual_summary", { length: len, power });
    g.visualTemps.textContent = tr("visual_temps", { hot, cold, delta: dt });
  }

  // ---- results: suitable sizes + order --------------------------------------

  function computePicks(): { picks: Pick[]; dt: number; power: number; matchedKw: number } {
    const dt = readDt();
    const power = Number(g.power.value);
    const col = Math.min(data.dtColumns.length - 1, Math.max(0, Math.round(Number(g.dt.value))));

    let row = 0;
    let matchedKw = data.dtKw[0]?.[col] ?? 0;
    for (let i = 0; i < data.dtKw.length; i++) {
      const v = data.dtKw[i]?.[col];
      if (v == null) continue;
      if (Math.abs(v - power) < Math.abs(matchedKw - power)) {
        matchedKw = v;
        row = i;
      }
    }
    const kwRow = data.kwPipe[Math.min(row, data.kwPipe.length - 1)] ?? [];
    const picks: Pick[] = [];
    for (let i = 0; i < kwRow.length; i++) {
      const d = kwRow[i];
      if (d != null) picks.push({ diameter: data.pipeColumns[i]!, dropPerM: d });
    }
    return { picks, dt, power, matchedKw };
  }

  function renderResults() {
    const { picks, dt, power, matchedKw } = computePicks();
    const avg = Number(g.avg.value);
    const len = Number(g.len.value);

    const summary = summaryRow(dt, power, matchedKw, picks.length);
    if (!picks.length) {
      g.results.innerHTML = summary + `<div class="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <i class="fa-solid fa-circle-exclamation mr-1"></i><span class="font-medium">${esc(s.none_title)}</span> — ${esc(s.none_hint)}</div>`;
      return;
    }
    g.results.innerHTML =
      summary +
      productsBlock(picks, len, avg) +
      `<h2 class="mt-6">${esc(s.engineering_title)}</h2>` +
      renderCards(picks, len, avg) +
      orderBlock();
  }

  // ---- matching catalogue products (filtered by computed diameter) ----------

  function uWertFor(p: PipeProduct): number | null {
    const table = p.type === "Duo" ? data.tempLossDuo : data.tempLossSingle;
    const row = table.find((x) => x.insulation === p.jacket && x.dimension === String(p.diameter));
    return row ? row.uWert : null;
  }

  function productsBlock(picks: Pick[], len: number, avg: number): string {
    const diameters = new Set(picks.map((p) => p.diameter));
    const products = data.products.filter((p) => diameters.has(p.diameter));
    if (!products.length) {
      return `<h2 class="mt-6">${esc(s.matched_products_title)}</h2>
        <div class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <i class="fa-solid fa-circle-exclamation mr-1"></i>${esc(s.no_match)}
        </div>`;
    }
    const hasLen = Number.isFinite(len) && len > 0;
    const hasTemp = Number.isFinite(avg) && avg > GROUND_TEMP;

    const cards = products
      .map((p) => {
        // Match the server's formatMoney (always de-DE) so per-m and total agree.
        const money = p.priceCents != null
          ? new Intl.NumberFormat("de-DE", { style: "currency", currency: p.currency })
          : null;
        const rows: [string, string][] = [
          [esc(s.pipe_diameter), `${p.diameter} mm`],
        ];
        if (p.jacket) rows.push([esc(s.pipe_jacket), esc(p.jacket)]);
        const u = uWertFor(p);
        if (u != null && hasLen && hasTemp) {
          rows.push([esc(s.pipe_energy_loss), `${nf0.format(Math.max(0, u * len * (avg - GROUND_TEMP)))} <span class="text-slate-400">${esc(s.unit_w)}</span>`]);
        }
        rows.push([
          esc(s.pipe_price_per_m),
          `${esc(p.priceText)}<span class="text-[10px] text-slate-400">${esc(s.per_meter)}${p.priceFallback ? ` · ${esc(s.price_fallback)}` : ""}</span>`,
        ]);
        if (money && p.priceCents != null && hasLen) {
          rows.push([esc(s.pipe_total), `<b class="text-slate-900">${money.format((p.priceCents / 100) * len)}</b>`]);
        }
        // Length goods: add the full computed run (metres) as one cart line.
        const buyable = hasLen && p.variantId && p.priceCents != null;
        const addBtn = buyable
          ? `<button type="button" data-add-cart data-vid="${p.variantId}" data-len="${Math.round(len)}"
               class="btn-primary mt-2 inline-flex items-center gap-1 text-xs">
               <i class="fa-solid fa-cart-plus"></i> ${esc(s.prod_add || "Add")} ${nf0.format(len)} m</button>`
          : "";
        return `<div class="card flex gap-4 p-4 transition hover:border-brand-300">
          <span class="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white">
            <img src="${esc(p.imageUrl || DEFAULT_IMAGE)}" alt="${esc(p.sku)}" loading="lazy" class="h-full w-full ${p.imageUrl ? "object-contain" : "object-cover"}"/>
          </span>
          <span class="min-w-0 flex-1">
            <span class="block text-base font-semibold text-slate-900">${esc(p.name)}</span>
            <span class="block text-xs text-slate-400">${esc(p.sku)}</span>
            <span class="mt-1 block space-y-0.5 text-sm">
              ${rows
                .map(([k, v]) => `<span class="flex justify-between gap-2"><span class="text-slate-500">${k}</span><span class="tabular-nums font-medium text-slate-800">${v}</span></span>`)
                .join("")}
            </span>
            <span class="mt-1 flex items-center gap-3">
              <a href="${esc(p.href)}" class="inline-flex items-center gap-1 text-xs font-medium text-brand-600">${esc(s.prod_view)} <i class="fa-solid fa-arrow-right"></i></a>
              ${addBtn}
            </span>
          </span>
        </div>`;
      })
      .join("");
    return `<h2 class="mt-6">${esc(s.matched_products_title)}</h2>
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">${cards}</div>`;
  }

  function summaryRow(dt: number, power: number, matchedKw: number, n: number): string {
    const approx =
      Math.abs(matchedKw - power) > 0.05
        ? ` <span class="text-[10px] text-slate-400">(${esc(s.summary_matched_kw)}: ${nf1.format(matchedKw)} ${esc(s.unit_kw)})</span>`
        : "";
    const rows: [string, string][] = [
      [esc(s.summary_dt), `<b class="text-slate-900">${dt}</b> K`],
      [esc(s.summary_required_kw), `<b class="text-slate-900">${nf1.format(power)}</b> ${esc(s.unit_kw)}${approx}`],
      [esc(s.summary_sizes), `<b class="text-slate-900">${n}</b>`],
    ];
    return `<div class="grid grid-cols-1 gap-1.5 rounded-md bg-slate-50 px-3 py-2 sm:grid-cols-3">
      ${rows
        .map(([k, v]) => `<div class="text-sm"><div class="text-[10px] uppercase tracking-wide text-slate-400">${k}</div><div class="tabular-nums text-slate-700">${v}</div></div>`)
        .join("")}
    </div>`;
  }

  function renderCards(picks: Pick[], len: number, avg: number): string {
    const minD = Math.min(...picks.map((p) => p.diameter));
    const minDrop = Math.min(...picks.map((p) => p.dropPerM));
    const hasLen = Number.isFinite(len) && len > 0;
    const hasTemp = Number.isFinite(avg) && avg > GROUND_TEMP;

    const cards = picks
      .map((p) => {
        const color = dropColor(p.dropPerM);
        const barW = Math.min(100, (p.dropPerM / 400) * 100);
        const u = uValueFor(p.diameter, data);

        const metrics: string[] = [];
        if (hasLen) {
          metrics.push(metric(s.card_drop_total, `${nf1.format((p.dropPerM * len) / 1000)} <span class="text-slate-400">${esc(s.unit_kpa)}</span>`));
        }
        metrics.push(
          u && hasTemp
            ? metric(s.card_heat_loss, `${nf0.format(u.uWert * len * (avg - GROUND_TEMP))} <span class="text-slate-400">${esc(s.unit_w)}</span> <span class="text-[10px] text-slate-400">(${esc(u.insulation)})</span>`)
            : metric(s.card_heat_loss, `<span class="text-slate-400">${esc(s.heat_loss_na)}</span>`),
        );

        const badges: string[] = [];
        if (p.diameter === minD) badges.push(badge("emerald", "star", s.recommended));
        if (p.dropPerM === minDrop && picks.length > 1) badges.push(badge("sky", "feather", s.badge_lowdrop));

        const r = 22;
        return `<div class="card p-4">
          <div class="flex items-center gap-3">
            <svg width="52" height="52" viewBox="0 0 52 52" role="img">
              <circle cx="26" cy="26" r="${r}" fill="${color}" fill-opacity="0.14" stroke="${color}" stroke-width="3"/>
              <circle cx="26" cy="26" r="${f(r * 0.6)}" fill="#fff" stroke="${color}" stroke-width="1"/>
            </svg>
            <div>
              <div class="text-lg font-semibold text-slate-900">Ø ${p.diameter} <span class="text-sm font-normal text-slate-400">mm</span></div>
              <div class="flex flex-wrap gap-1">${badges.join("")}</div>
            </div>
          </div>
          <div class="mt-3">
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500">${esc(s.card_drop)}</span>
              <span class="font-semibold tabular-nums" style="color:${color}">${nf0.format(p.dropPerM)} ${esc(s.unit_pa_m)}</span>
            </div>
            <div class="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div class="h-full rounded-full" style="width:${f(barW)}%;background:${color}"></div>
            </div>
          </div>
          <div class="mt-3 grid grid-cols-2 gap-2">${metrics.join("")}</div>
        </div>`;
      })
      .join("");
    return `<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">${cards}</div>`;
  }

  function metric(label: string | undefined, value: string): string {
    return `<div class="rounded-md bg-slate-50 px-2.5 py-1.5">
      <div class="text-[10px] uppercase tracking-wide text-slate-400">${esc(label)}</div>
      <div class="text-sm font-medium tabular-nums text-slate-800">${value}</div>
    </div>`;
  }

  function badge(tone: string, icon: string, label: string | undefined): string {
    const cls: Record<string, string> = {
      emerald: "bg-emerald-100 text-emerald-700",
      sky: "bg-sky-100 text-sky-700",
    };
    return `<span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${cls[tone] ?? cls.emerald}"><i class="fa-solid fa-${icon}"></i>${esc(label)}</span>`;
  }

  function orderBlock(): string {
    const { phone, email } = data.contact;
    const tel = phone.replace(/[^+\d]/g, "");
    return `<div class="mt-6 flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-5 text-center">
      <p class="text-sm text-slate-600">${esc(s.order_intro)}</p>
      <div class="flex flex-wrap justify-center gap-3">
        <a href="tel:${esc(tel)}" class="btn-primary"><i class="fa-solid fa-phone"></i>${esc(s.order_call)}</a>
        <a href="mailto:${esc(email)}" class="btn"><i class="fa-solid fa-envelope"></i>${esc(s.order_mail)}</a>
      </div>
    </div>`;
  }
}

// ---- helpers ----------------------------------------------------------------

// Cold (20 °C) blue → hot (80 °C) red. Mirrors the mobile temp-to-colour ramp.
function tempColor(temp: number): string {
  const t = clamp01((temp - 20) / 60);
  const r = Math.round(100 + 155 * t);
  const gC = Math.round(150 * (1 - t) + 80 * t);
  const b = Math.round(255 * (1 - t) + 80 * t);
  return `rgb(${r},${gC},${b})`;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function uValueFor(diameter: number, data: PipeConfigData): TempLoss | null {
  const matches = data.tempLossSingle.filter((r) => Number(r.dimension) === diameter);
  if (!matches.length) return null;
  return matches.reduce((a, b) => (b.uWert < a.uWert ? b : a));
}

function dropColor(drop: number): string {
  if (drop <= 150) return "#16a34a";
  if (drop <= 300) return "#d97706";
  return "#dc2626";
}

function f(n: number): number {
  return Math.round(n * 100) / 100;
}

function parseData(elx: HTMLElement): PipeConfigData {
  const empty: PipeConfigData = {
    dtColumns: [], pipeColumns: [], dtKw: [], kwPipe: [],
    tempLossSingle: [], tempLossDuo: [], products: [], contact: { phone: "", email: "" }, lang: "en", strings: {},
  };
  try {
    return { ...empty, ...(JSON.parse(elx.textContent || "{}") as Partial<PipeConfigData>) } as PipeConfigData;
  } catch {
    return empty;
  }
}

function esc(s: string | undefined): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
